import React, { useContext, useEffect, useState } from "react";

import { Error } from "@mui/icons-material";
import {
  Alert,
  Box,
  DialogContent,
  Grid,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import ConfirmDialog from "../shared/confirm-dialog";
import { updateEmail, updateUsername } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import DisplayError from "components/shared/display-error";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { UserContext } from "context/user-context";
import { Validator } from "helpers/validator";

type DialogMode = "edit" | "emailless";

interface UpdateUserEmailDialogProps {
  userId: string;
  userEmail: string;
  regionCode: string;
  orgsCount: number;
  open: boolean;
  setOpen: (newOpen: boolean) => void;
  mode?: DialogMode;
}

const UpdateUserEmailDialog = ({
  userId,
  userEmail,
  regionCode,
  orgsCount,
  open,
  setOpen,
  mode = "edit",
}: UpdateUserEmailDialogProps) => {
  const isEmaillessMode = mode === "emailless";
  const [updatedValue, setUpdatedValue] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [successOpen, setSuccessOpen] = useState(false);
  const validator = new Validator();
  const theme = useTheme();

  const [validationError, setValidationError] = useState("");
  const { user, setUser } = useContext(UserContext);
  const { showError } = useContext(SnackbarContext);

  const handleSuccessLocalUpdate = (newValue: string) => {
    const updatedUser = new User({
      ...user,
    });
    updatedUser.email = newValue;
    setUser(updatedUser);
  };

  const handleSubmit = () => {
    const headers = callOptionsHeaders(regionCode, userId);

    if (isEmaillessMode) {
      // Auto-detect: a valid email submits as updateEmail (conversion);
      // anything else that passes the username validator submits as
      // updateUsername (rename). Both fail → surface the username error.
      const emailValid = validator.validateEmail(updatedValue) === undefined;
      if (emailValid) {
        const submitted = updatedValue;
        setUpdatedValue("");
        updateEmail({ userId, email: submitted }, headers)
          .then(() => {
            setSuccessMessage("Successfully added user email");
            setSuccessOpen(true);
            handleSuccessLocalUpdate(submitted);
          })
          .catch(showError);
        return;
      }
      const usernameError = validator.validateUsername(updatedValue);
      if (usernameError) {
        setValidationError(usernameError);
        setOpen(true);
        return;
      }
      const submitted = updatedValue;
      setUpdatedValue("");
      updateUsername({ userId, username: submitted }, headers)
        .then(() => {
          setSuccessMessage("Successfully updated username");
          setSuccessOpen(true);
          handleSuccessLocalUpdate(submitted);
        })
        .catch(showError);
      return;
    }

    // mode === "edit": email-only path for users who already have a real email
    const emailError = validator.validateEmail(updatedValue);
    if (emailError) {
      setValidationError(emailError);
      setOpen(true);
      return;
    }
    const submitted = updatedValue;
    setUpdatedValue("");
    updateEmail({ userId, email: submitted.toLowerCase() }, headers)
      .then(() => {
        setSuccessMessage("Successfully updated user email");
        setSuccessOpen(true);
        handleSuccessLocalUpdate(submitted);
      })
      .catch(showError);
  };

  useEffect(() => {
    setValidationError("");
  }, [updatedValue]);

  const title = isEmaillessMode ? "Update Email or Username" : "Update User Email";
  const fieldLabel = isEmaillessMode ? "New Email or Username" : "New Email";
  const fieldTestId = isEmaillessMode
    ? "update-user-identifier"
    : "update-user-email";
  const snackbarTestId = isEmaillessMode
    ? "update-identifier-dialog-success"
    : "update-email-dialog-success";
  const warningCopy = isEmaillessMode
    ? "Customer will be required to log in again on all platforms (mobile and web) with the new identifier."
    : "This does not trigger confirmation email. Customer will be required to log in again on all platforms (mobile and web).";

  return (
    <>
      <ConfirmDialog
        title={title}
        open={open}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Update"
        setOpen={setOpen}
        width={"md"}
        onConfirm={() => handleSubmit()}
      >
        <DialogContent style={{ paddingBottom: "0" }}>
          <Grid
            item
            sm={12}
            container
            direction="column"
            justifyContent="center"
          >
            <Box
              sx={{ width: 1 }}
              bgcolor={theme.palette.warning.main}
              borderRadius="5px"
              border={1}
              padding={1}
              marginBottom={2}
              borderColor={theme.palette.warning.main}
            >
              <Grid container alignItems="center">
                <Grid item xs={2} container justifyContent="center">
                  <Error fontSize="large" />
                </Grid>
                <Grid item xs={10} justifyContent="center">
                  {warningCopy}
                </Grid>
              </Grid>
            </Box>
            {!isEmaillessMode && orgsCount > 1 && (
              <Box
                sx={{ width: 1 }}
                bgcolor={theme.palette.warning.main}
                borderRadius="5px"
                border={1}
                padding={1}
                marginBottom={2}
                borderColor={theme.palette.warning.main}
              >
                <Grid container alignItems="center">
                  <Grid item xs={2} container justifyContent="center">
                    <Error fontSize="large" />
                  </Grid>
                  <Grid item xs={10} justifyContent="center">
                    {`This is a Multi-Org user in ${orgsCount} organisations.`}
                    <br />
                    The email will be changed in all organisations that the user
                    is in.
                  </Grid>
                </Grid>
              </Box>
            )}
            <Typography
              variant="body2"
              display="inline"
              style={{
                whiteSpace: "normal",
                wordBreak: "break-all",
              }}
            >
              {isEmaillessMode ? (
                <>
                  {`Current identifier: ${userEmail}. `}
                  Enter a valid email address to allow the user to log in 
                  with an email and password. Enter a username to allow 
                  them to log in without an email.
                </>
              ) : (
                `You are about to update user email ${userEmail}`
              )}
            </Typography>
          </Grid>
          <TextField
            margin="normal"
            id="update-user"
            label={fieldLabel}
            value={updatedValue}
            onChange={(e) => setUpdatedValue(e.target.value)}
            variant="outlined"
            size="small"
            style={{ width: "100%" }}
            inputProps={{ "data-testid": fieldTestId }}
          />
          <DisplayError errorMessage={validationError} align="left" />
        </DialogContent>
      </ConfirmDialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={successOpen}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessOpen(false)}
        data-testid={snackbarTestId}
      >
        <Alert severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
export default UpdateUserEmailDialog;
